import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Building2,
  Languages,
  CalendarDays,
  MapPin,
  Loader2,
  LogOut,
  CheckCircle2,
  Clock,
  XCircle,
  Video,
  CreditCard,
  Plus,
  Trash2,
  ClipboardList,
  Users,
  Check,
  NotebookPen,
} from "lucide-react";
import NotificationBell from "../components/NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "soarlife-clinic-token";
const CLINIC_KEY = "soarlife-clinic";

const APPOINTMENT_TYPES = [
  "New patient consult",
  "Ultrasound / monitoring",
  "IUI procedure",
  "Egg retrieval",
  "Embryo transfer",
  "Follow-up",
  "Other",
];

const STATUS_STYLE = {
  pending: { label: "Pending", cls: "bg-[#AE8F78]/15 text-[#875F45] border-[#AE8F78]/40", icon: Clock },
  confirmed: { label: "Confirmed", cls: "bg-[#3F5043]/10 text-[#3F5043] border-[#3F5043]/30", icon: CheckCircle2 },
  completed: { label: "Completed", cls: "bg-[#899480]/15 text-[#899480] border-[#899480]/40", icon: CheckCircle2 },
  canceled: { label: "Canceled", cls: "bg-[#875F45]/10 text-[#875F45]/70 border-[#875F45]/20", icon: XCircle },
};

function useClinic() {
  const [clinic, setClinic] = useState(() => {
    try {
      const c = localStorage.getItem(CLINIC_KEY);
      return c ? JSON.parse(c) : null;
    } catch {
      return null;
    }
  });
  const authHeader = () => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const setAuth = (data) => {
    if (data.token) localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(CLINIC_KEY, JSON.stringify(data.clinic));
    setClinic(data.clinic);
  };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CLINIC_KEY);
    setClinic(null);
  };
  return { clinic, setAuth, logout, authHeader, setClinic };
}

export default function Clinic() {
  const { clinic, setAuth, logout, authHeader, setClinic } = useClinic();

  // Refresh status (pending → approved) on load
  useEffect(() => {
    if (!clinic) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/clinic/me`, { headers: authHeader() });
        if (res.data && res.data.status !== clinic.status) {
          const merged = { ...clinic, ...res.data };
          localStorage.setItem(CLINIC_KEY, JSON.stringify(merged));
          setClinic(merged);
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic?.id]);

  if (!clinic) return <ClinicAuth onAuth={setAuth} />;
  return <ClinicDashboard clinic={clinic} authHeader={authHeader} onLogout={logout} />;
}

function ClinicAuth({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ clinic_name: "", contact_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [registered, setRegistered] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      if (mode === "login") {
        const res = await axios.post(`${API}/clinic/login`, { email: form.email, password: form.password });
        onAuth(res.data);
      } else {
        await axios.post(`${API}/clinic/register`, form);
        setRegistered(true);
      }
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#EDEDEE] rounded-2xl px-4 py-3 text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div className="min-h-screen bg-[#3F5043] flex items-center justify-center p-5">
      <div data-testid="clinic-auth-screen" className="w-full max-w-md bg-[#F8F7F3] rounded-3xl p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#3F5043] flex items-center justify-center mb-4">
            <Building2 size={22} className="text-[#AE8F78]" />
          </div>
          <div className="font-serif italic text-3xl text-[#3F5043] leading-tight text-center">
            Soarlife for Clinics
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mt-1 text-center">
            Interpreter Partner Portal
          </div>
          <p className="text-sm text-[#3F5043]/70 leading-relaxed mt-3 text-center">
            Request Haitian Creole or French interpreters for your fertility patients — virtual anywhere in the U.S., in person in Orlando.
          </p>
        </div>

        {registered ? (
          <div data-testid="clinic-register-success" className="text-center py-4">
            <CheckCircle2 size={40} className="text-[#899480] mx-auto mb-4" strokeWidth={1.2} />
            <div className="font-serif text-xl text-[#3F5043] mb-2">Request received</div>
            <p className="text-sm text-[#3F5043]/70 mb-5">
              Soarlife will review and approve your clinic account. Sign in to check your status.
            </p>
            <button
              onClick={() => { setRegistered(false); setMode("login"); }}
              data-testid="clinic-goto-login"
              className="inline-flex items-center justify-center rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors"
            >
              Sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <input data-testid="clinic-input-name" required placeholder="Clinic name" value={form.clinic_name}
                  onChange={(e) => setForm({ ...form, clinic_name: e.target.value })} className={inputCls} />
                <input data-testid="clinic-input-contact" required placeholder="Contact person" value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} />
                <input data-testid="clinic-input-phone" placeholder="Phone (optional)" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
              </>
            )}
            <input data-testid="clinic-input-email" type="text" required placeholder="Work email or username" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
            <input data-testid="clinic-input-password" type="password" required minLength={8} placeholder="Password (8+ characters)" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            {err && (
              <div data-testid="clinic-auth-error" className="text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2">
                {err}
              </div>
            )}
            <button type="submit" disabled={loading} data-testid="clinic-auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : mode === "login" ? "Sign in" : "Request clinic account"}
            </button>
            <button type="button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
              data-testid="clinic-auth-toggle"
              className="w-full text-center text-xs uppercase tracking-[0.2em] text-[#899480] hover:text-[#3F5043] pt-1">
              {mode === "login" ? "New clinic? Request an account" : "Already approved? Sign in"}
            </button>
          </form>
        )}
        <div className="mt-5 text-center text-xs text-[#3F5043]/50">
          Patient-facing? <a href="/portal" className="text-[#875F45] underline">Go to the Patient Portal</a>
        </div>
      </div>
    </div>
  );
}

function ClinicDashboard({ clinic, authHeader, onLogout }) {
  const [requests, setRequests] = useState([]);
  const [interpPhotos, setInterpPhotos] = useState({});
  const [tab, setTab] = useState("overview");
  const approved = clinic.status === "approved";
  const subscribed = ["active", "grandfathered"].includes(clinic.subscription_status);
  const fullAccess = approved && subscribed;

  const load = async () => {
    try {
      const res = await axios.get(`${API}/clinic/requests`, { headers: authHeader() });
      const list = res.data || [];
      setRequests(list);
      const next = {};
      for (const r of list) {
        const p = r.interpreter;
        if (p?.has_photo && !next[p.id]) {
          try {
            const img = await axios.get(`${API}/interpreters/${p.id}/photo`, {
              headers: authHeader(), responseType: "blob",
            });
            next[p.id] = URL.createObjectURL(img.data);
          } catch {}
        }
      }
      setInterpPhotos(next);
    } catch {}
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="clinic-dashboard">
      <header className="bg-[#3F5043] text-[#F8F7F3] px-5 sm:px-8 py-5 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 size={20} className="text-[#AE8F78]" />
            <div>
              <div className="font-serif italic text-xl leading-tight">{clinic.clinic_name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#AE8F78] font-semibold">
                Interpreter Partner Portal
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell authHeader={authHeader} basePath="/clinic/notifications" dark />
            <span
              data-testid="clinic-status-badge"
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full border ${
                approved
                  ? "bg-[#899480]/20 text-[#F8F7F3] border-[#899480]/50"
                  : "bg-[#AE8F78]/20 text-[#F8F7F3] border-[#AE8F78]/50"
              }`}
            >
              {approved ? "Approved" : "Pending approval"}
            </span>
            <span
              data-testid="clinic-subscription-badge"
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full border ${
                subscribed
                  ? "bg-[#AE8F78]/20 text-[#F8F7F3] border-[#AE8F78]/50"
                  : "bg-red-900/30 text-[#F8F7F3] border-red-300/40"
              }`}
            >
              {clinic.subscription_status === "grandfathered"
                ? "Legacy partner"
                : clinic.subscription_status === "active"
                  ? "Partner plan"
                  : "No plan"}
            </span>
            <button onClick={onLogout} data-testid="clinic-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8F7F3]/10 text-[#F8F7F3] text-xs hover:bg-[#F8F7F3]/20">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      {fullAccess && (
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex gap-2 flex-wrap" data-testid="clinic-tabs">
          {[["overview", "Overview"], ["interpreters", "Interpreters"], ["patients", "Patients"]].map(([k, l]) => (
            <button key={k} data-testid={`clinic-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-semibold transition-all ${
                tab === k
                  ? "bg-[#3F5043] text-[#F8F7F3]"
                  : "bg-white border border-[#BCB3A6]/40 text-[#3F5043]/60 hover:text-[#3F5043]"
              }`}>
              {l}
            </button>
          ))}
        </nav>
      )}

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {fullAccess && tab === "overview" && <ClinicOverview authHeader={authHeader} />}
        {fullAccess && tab === "patients" && <ClinicPatients authHeader={authHeader} />}
        {(!fullAccess || tab === "interpreters") && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          {!approved ? (
            <div data-testid="clinic-pending-card" className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-8 text-center">
              <Clock size={36} className="text-[#AE8F78] mx-auto mb-4" strokeWidth={1.2} />
              <div className="font-serif italic text-2xl text-[#3F5043] mb-2">Approval in progress</div>
              <p className="text-sm text-[#3F5043]/70 leading-relaxed">
                Soarlife is reviewing your clinic account. Once approved, you'll request Haitian Creole and French interpreters here.
                Questions? Call (407) 374-9834.
              </p>
            </div>
          ) : !subscribed ? (
            <SubscribeCard authHeader={authHeader} />
          ) : (
            <RequestForm authHeader={authHeader} onCreated={load} />
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-4">
            Your interpreter requests
          </div>
          <div data-testid="clinic-requests-list" className="space-y-3">
            {requests.length === 0 && (
              <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#3F5043]/60">
                No requests yet.
              </div>
            )}
            {requests.map((r, i) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
              const StIcon = st.icon;
              return (
                <div key={r.id} data-testid={`clinic-request-${i}`}
                  className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-serif text-lg text-[#3F5043]">{r.appointment_type}</div>
                      <div className="text-xs text-[#3F5043]/60 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                        <span className="inline-flex items-center gap-1"><Languages size={12} /> {r.language === "ht" ? "Haitian Creole" : "French"}</span>
                        <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {r.appointment_date} · {r.appointment_time}</span>
                        <span className="inline-flex items-center gap-1">
                          {r.mode === "virtual" ? <Video size={12} /> : <MapPin size={12} />}
                          {r.mode === "virtual" ? "Virtual" : r.location}
                        </span>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-semibold px-3 py-1.5 rounded-full border ${st.cls}`}>
                      <StIcon size={11} /> {st.label}
                    </span>
                  </div>
                  <div className="text-xs text-[#3F5043]/60">Patient ref: {r.patient_ref}</div>
                  {r.interpreter && (
                    <div data-testid={`clinic-interp-${i}`}
                      className="mt-3 flex items-center gap-3 bg-[#899480]/10 border border-[#899480]/30 rounded-2xl px-4 py-3">
                      {interpPhotos[r.interpreter.id] ? (
                        <img src={interpPhotos[r.interpreter.id]} alt={r.interpreter.full_name}
                          className="w-10 h-10 rounded-full object-cover border border-[#BCB3A6]/40" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#3F5043] text-[#AE8F78] flex items-center justify-center font-serif text-sm shrink-0">
                          {r.interpreter.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-[#3F5043]">
                          Your interpreter: {r.interpreter.full_name}
                        </div>
                        <div className="text-[11px] text-[#3F5043]/60">
                          {r.interpreter.languages.map((l) => (l === "ht" ? "Haitian Creole" : "French")).join(" · ")}
                          {r.interpreter.bio ? ` — ${r.interpreter.bio}` : ""}
                        </div>
                      </div>
                    </div>
                  )}
                  {r.admin_note && (
                    <div className="mt-2 text-xs bg-[#3F5043]/5 text-[#3F5043] rounded-xl px-3 py-2">
                      Soarlife: {r.admin_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>
        )}
      </main>
    </div>
  );
}

function SubscribeCard({ authHeader }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const subscribe = async () => {
    if (loading) return;
    setLoading(true);
    setErr("");
    try {
      const res = await axios.post(
        `${API}/clinic/subscribe`,
        { origin_url: window.location.origin },
        { headers: authHeader() }
      );
      window.location.href = res.data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout — please try again.");
      setLoading(false);
    }
  };
  return (
    <div data-testid="clinic-subscribe-card" className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-8 text-center">
      <CreditCard size={36} className="text-[#AE8F78] mx-auto mb-4" strokeWidth={1.2} />
      <div className="font-serif italic text-2xl text-[#3F5043] mb-3">Clinic Partner Plan</div>
      <div className="font-serif text-4xl text-[#3F5043] mb-3">
        $199<span className="text-base text-[#3F5043]/50">/month</span>
      </div>
      <p className="text-sm text-[#3F5043]/70 leading-relaxed mb-5">
        Unlimited Haitian Creole &amp; French interpreter requests, priority scheduling, and your
        partner dashboard. Secure checkout by Stripe — cancel anytime.
      </p>
      {err && <p className="text-xs text-red-700 mb-3" data-testid="clinic-subscribe-error">{err}</p>}
      <button
        onClick={subscribe}
        disabled={loading}
        data-testid="clinic-subscribe-btn"
        className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 bg-[#3F5043] text-[#F8F7F3] text-sm font-medium hover:bg-[#875F45] transition-colors disabled:opacity-40"
      >
        {loading ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />}
        Subscribe — $199/mo
      </button>
    </div>
  );
}

function RequestForm({ authHeader, onCreated }) {
  const [form, setForm] = useState({
    language: "ht",
    appointment_date: "",
    appointment_time: "",
    mode: "virtual",
    location: "",
    appointment_type: APPOINTMENT_TYPES[0],
    patient_ref: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setOk(false);
    try {
      await axios.post(`${API}/clinic/requests`, form, { headers: authHeader() });
      setOk(true);
      setForm({ ...form, appointment_date: "", appointment_time: "", patient_ref: "", notes: "", location: "" });
      onCreated();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit the request.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#F8F7F3] border border-[#BCB3A6]/50 rounded-2xl px-4 py-3 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]";
  const pill = (active) =>
    `px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
      active ? "bg-[#3F5043] text-[#F8F7F3]" : "bg-[#EDEDEE] text-[#3F5043]/70 hover:bg-[#BCB3A6]/30"
    }`;

  return (
    <form onSubmit={submit} data-testid="interpreter-request-form"
      className="bg-white rounded-3xl border border-[#BCB3A6]/40 p-7">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-1">
        New request
      </div>
      <div className="font-serif italic text-2xl text-[#3F5043] mb-5">Request an interpreter</div>

      <div className="space-y-4">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Language</label>
          <div className="flex gap-2">
            <button type="button" data-testid="req-language-ht" onClick={() => setForm({ ...form, language: "ht" })} className={pill(form.language === "ht")}>
              Kreyòl Ayisyen
            </button>
            <button type="button" data-testid="req-language-fr" onClick={() => setForm({ ...form, language: "fr" })} className={pill(form.language === "fr")}>
              Français
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Date</label>
            <input type="date" required data-testid="req-date" value={form.appointment_date}
              onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Time</label>
            <input type="time" required data-testid="req-time" value={form.appointment_time}
              onChange={(e) => setForm({ ...form, appointment_time: e.target.value })} className={inputCls} />
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Format</label>
          <div className="flex gap-2">
            <button type="button" data-testid="req-mode-virtual" onClick={() => setForm({ ...form, mode: "virtual" })} className={pill(form.mode === "virtual")}>
              Virtual
            </button>
            <button type="button" data-testid="req-mode-in-person" onClick={() => setForm({ ...form, mode: "in_person" })} className={pill(form.mode === "in_person")}>
              In person (Orlando)
            </button>
          </div>
        </div>

        {form.mode === "in_person" && (
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Clinic address</label>
            <input required data-testid="req-location" placeholder="Street, city, suite" value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
          </div>
        )}

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Appointment type</label>
          <select data-testid="req-type" value={form.appointment_type}
            onChange={(e) => setForm({ ...form, appointment_type: e.target.value })} className={inputCls}>
            {APPOINTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
            Patient reference (initials or internal ID — no full names)
          </label>
          <input required data-testid="req-patient-ref" placeholder="e.g. J.M. or chart #4471" maxLength={60} value={form.patient_ref}
            onChange={(e) => setForm({ ...form, patient_ref: e.target.value })} className={inputCls} />
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">Notes for the interpreter (optional)</label>
          <textarea data-testid="req-notes" rows={3} maxLength={1000} value={form.notes}
            placeholder="Anything that helps us prepare — no medical records here."
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className={`${inputCls} resize-none`} />
        </div>

        {err && (
          <div data-testid="req-error" className="text-sm text-[#875F45] bg-[#875F45]/10 border border-[#875F45]/30 rounded-xl px-3 py-2">{err}</div>
        )}
        {ok && (
          <div data-testid="req-success" className="text-sm text-[#3F5043] bg-[#899480]/15 border border-[#899480]/40 rounded-xl px-3 py-2">
            Request sent — Soarlife will confirm your interpreter shortly.
          </div>
        )}

        <button type="submit" disabled={loading} data-testid="req-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 bg-[#875F45] text-[#F8F7F3] text-sm font-medium hover:bg-[#3F5043] transition-colors disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : "Send request"}
        </button>
      </div>
    </form>
  );
}

function ClinicOverview({ authHeader }) {
  const [metrics, setMetrics] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", due: "", assignee: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/clinic/dashboard`, { headers: authHeader() }),
        axios.get(`${API}/clinic/tasks`, { headers: authHeader() }),
      ]);
      setMetrics(m.data);
      setTasks(t.data);
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTask = async (e) => {
    e.preventDefault();
    if (saving || !taskForm.title.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/clinic/tasks`, taskForm, { headers: authHeader() });
      setTaskForm({ title: "", due: "", assignee: "" });
      await load();
    } finally { setSaving(false); }
  };

  const toggle = async (id) => {
    await axios.patch(`${API}/clinic/tasks/${id}`, {}, { headers: authHeader() }).catch(() => {});
    load();
  };

  const removeTask = async (id) => {
    await axios.delete(`${API}/clinic/tasks/${id}`, { headers: authHeader() }).catch(() => {});
    load();
  };

  const CARDS = metrics ? [
    { label: "Active patients", value: metrics.active_patients, testid: "cm-patients" },
    { label: "Pending interpreter requests", value: metrics.pending_requests, testid: "cm-pending" },
    { label: "Upcoming interpreter appts", value: metrics.upcoming_interpreter_appts, testid: "cm-upcoming" },
    { label: "Open tasks", value: metrics.open_tasks, testid: "cm-tasks" },
    { label: "Active treatment plans", value: metrics.active_plans, testid: "cm-plans" },
    { label: "Completed requests", value: metrics.completed_requests, testid: "cm-completed" },
  ] : [];

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="clinic-overview">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Clinic dashboard</div>
      {!metrics ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          {CARDS.map((c) => (
            <div key={c.label} data-testid={c.testid} className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-4 border-l-4 border-l-[#3F5043]">
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#899480] font-semibold mb-1.5 leading-tight">{c.label}</div>
              <div className="font-serif text-2xl text-[#3F5043]">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Team tasks</div>
      <form onSubmit={addTask} data-testid="clinic-task-form" className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-5 flex flex-wrap gap-3">
        <input data-testid="task-title" required placeholder="Task (e.g. Call patient re: consent forms)" value={taskForm.title}
          onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className={`${inputCls} flex-1 min-w-[200px]`} />
        <input data-testid="task-due" type="date" value={taskForm.due}
          onChange={(e) => setTaskForm({ ...taskForm, due: e.target.value })} className={inputCls} />
        <input data-testid="task-assignee" placeholder="Assignee (optional)" value={taskForm.assignee}
          onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })} className={inputCls} />
        <button type="submit" data-testid="task-add-btn" disabled={saving}
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add
        </button>
      </form>
      <div className="space-y-2" data-testid="clinic-task-list">
        {tasks.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-6 text-center text-sm text-[#899480]">No tasks yet.</div>
        )}
        {tasks.map((t) => (
          <div key={t.id} data-testid={`clinic-task-${t.id}`}
            className="flex items-center gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-4 py-3">
            <button onClick={() => toggle(t.id)} data-testid={`task-toggle-${t.id}`}
              className={`w-6 h-6 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                t.done ? "bg-[#899480] border-[#899480] text-white" : "border-[#BCB3A6] text-transparent hover:border-[#899480]"
              }`} aria-label="Toggle task">
              <Check size={13} />
            </button>
            <div className={`flex-1 text-sm ${t.done ? "line-through text-[#899480]" : "text-[#3F5043]"}`}>
              {t.title}
              <span className="block text-[11px] text-[#899480]">
                {t.due ? `Due ${t.due}` : ""}{t.assignee ? ` · ${t.assignee}` : ""}
              </span>
            </div>
            <button onClick={() => removeTask(t.id)} data-testid={`task-delete-${t.id}`}
              className="p-2 rounded-full text-[#875F45]/70 hover:bg-[#875F45]/10" aria-label="Delete task">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ClinicPatients({ authHeader }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: "", email: "", pathway: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/clinic/patients`, { headers: authHeader() });
      setPatients(res.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setErr("");
    try {
      await axios.post(`${API}/clinic/patients`, form, { headers: authHeader() });
      setForm({ full_name: "", email: "", pathway: "", notes: "" });
      await load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not add the patient.");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/clinic/patients/${id}`, { headers: authHeader() }).catch(() => {});
    if (openId === id) setOpenId(null);
    load();
  };

  const inputCls =
    "bg-white border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div data-testid="clinic-patients">
      <div className="text-xs uppercase tracking-[0.25em] text-[#899480] font-semibold mb-3">Patient management</div>
      <form onSubmit={add} data-testid="patient-form" className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input data-testid="patient-name" required placeholder="Patient full name" value={form.full_name}
          onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
        <input data-testid="patient-email" required type="email" placeholder="Patient email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <select data-testid="patient-pathway" value={form.pathway}
          onChange={(e) => setForm({ ...form, pathway: e.target.value })} className={inputCls}>
          <option value="">Pathway (optional)…</option>
          <option value="ivf">IVF</option>
          <option value="iui">IUI</option>
          <option value="egg_freezing">Egg freezing</option>
          <option value="donor_egg">Donor egg</option>
          <option value="natural">Natural / TTC</option>
        </select>
        <input data-testid="patient-notes" placeholder="Quick note (optional)" value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
        <div className="sm:col-span-2 flex items-center gap-3">
          <button type="submit" data-testid="patient-add-btn" disabled={saving}
            className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add patient
          </button>
          {err && <span className="text-xs text-[#875F45]" data-testid="patient-error">{err}</span>}
        </div>
      </form>

      {loading ? (
        <div className="text-center py-10"><Loader2 size={18} className="animate-spin inline text-[#899480]" /></div>
      ) : patients.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#BCB3A6]/40 p-8 text-center text-sm text-[#899480]" data-testid="patients-empty">
          No patients yet — add your first above.
        </div>
      ) : (
        <div className="space-y-2">
          {patients.map((p) => (
            <div key={p.id} data-testid={`patient-row-${p.id}`}
              className="bg-white border border-[#BCB3A6]/40 rounded-2xl px-5 py-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button onClick={() => setOpenId(openId === p.id ? null : p.id)} data-testid={`patient-open-${p.id}`}
                  className="text-left">
                  <div className="text-sm font-medium text-[#3F5043]">
                    {p.full_name}
                    {p.soarlife_client && (
                      <span className="ml-2 text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-full bg-[#899480]/15 text-[#3F5043]">
                        Soarlife client
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[#899480]">
                    {p.email}{p.pathway ? ` · ${p.pathway.replace(/_/g, " ").toUpperCase()}` : ""}
                    {` · ${p.notes_count} notes · ${p.active_plans} active plans`}
                  </div>
                </button>
                <button onClick={() => remove(p.id)} data-testid={`patient-delete-${p.id}`}
                  className="p-2 rounded-full text-[#875F45]/70 hover:bg-[#875F45]/10" aria-label="Remove patient">
                  <Trash2 size={14} />
                </button>
              </div>
              {p.notes && <div className="text-xs text-[#3F5043]/60 mt-1.5">{p.notes}</div>}
              {openId === p.id && <PatientDetail pid={p.id} authHeader={authHeader} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PatientDetail({ pid, authHeader }) {
  const [notes, setNotes] = useState([]);
  const [plans, setPlans] = useState([]);
  const [note, setNote] = useState({ kind: "clinical", content: "" });
  const [plan, setPlan] = useState({ title: "", pathway: "", start_date: "", details: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [n, pl] = await Promise.all([
        axios.get(`${API}/clinic/patients/${pid}/notes`, { headers: authHeader() }),
        axios.get(`${API}/clinic/patients/${pid}/plans`, { headers: authHeader() }),
      ]);
      setNotes(n.data);
      setPlans(pl.data);
    } catch {}
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  const addNote = async (e) => {
    e.preventDefault();
    if (busy || !note.content.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/clinic/patients/${pid}/notes`, note, { headers: authHeader() });
      setNote({ kind: "clinical", content: "" });
      await load();
    } finally { setBusy(false); }
  };

  const addPlan = async (e) => {
    e.preventDefault();
    if (busy || !plan.title.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/clinic/patients/${pid}/plans`, plan, { headers: authHeader() });
      setPlan({ title: "", pathway: "", start_date: "", details: "" });
      await load();
    } finally { setBusy(false); }
  };

  const setPlanStatus = async (id, status) => {
    await axios.patch(`${API}/clinic/plans/${id}`, { status }, { headers: authHeader() }).catch(() => {});
    load();
  };

  const inputCls =
    "bg-[#F8F7F3] border border-[#BCB3A6]/50 rounded-2xl px-3 py-2 text-sm text-[#3F5043] focus:outline-none focus:ring-2 focus:ring-[#899480]";

  return (
    <div className="mt-4 pt-4 border-t border-[#BCB3A6]/30 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid={`patient-detail-${pid}`}>
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
          <NotebookPen size={12} /> Clinical & provider notes
        </div>
        <form onSubmit={addNote} className="flex gap-2 mb-3">
          <select value={note.kind} data-testid={`note-kind-${pid}`}
            onChange={(e) => setNote({ ...note, kind: e.target.value })} className={inputCls}>
            <option value="clinical">Clinical</option>
            <option value="provider">Provider</option>
          </select>
          <input placeholder="Add a note…" value={note.content} data-testid={`note-input-${pid}`}
            onChange={(e) => setNote({ ...note, content: e.target.value })} className={`${inputCls} flex-1`} />
          <button type="submit" data-testid={`note-add-${pid}`} disabled={busy}
            className="rounded-full px-4 py-2 bg-[#3F5043] text-[#F8F7F3] text-xs font-semibold hover:bg-[#875F45] disabled:opacity-60">
            Add
          </button>
        </form>
        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-row-${n.id}`} className="text-xs bg-[#F8F7F3] rounded-xl px-3 py-2 text-[#3F5043]/80">
              <span className={`text-[9px] uppercase tracking-[0.12em] font-bold mr-2 ${n.kind === "clinical" ? "text-[#875F45]" : "text-[#899480]"}`}>
                {n.kind}
              </span>
              {n.content}
            </div>
          ))}
          {notes.length === 0 && <div className="text-xs text-[#899480]">No notes yet.</div>}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#899480] font-semibold mb-2">
          <ClipboardList size={12} /> Treatment plans
        </div>
        <form onSubmit={addPlan} className="flex gap-2 mb-3 flex-wrap">
          <input placeholder="Plan title (e.g. IVF Cycle 1)" value={plan.title} data-testid={`plan-title-${pid}`}
            onChange={(e) => setPlan({ ...plan, title: e.target.value })} className={`${inputCls} flex-1 min-w-[140px]`} />
          <input type="date" value={plan.start_date} data-testid={`plan-date-${pid}`}
            onChange={(e) => setPlan({ ...plan, start_date: e.target.value })} className={inputCls} />
          <button type="submit" data-testid={`plan-add-${pid}`} disabled={busy}
            className="rounded-full px-4 py-2 bg-[#3F5043] text-[#F8F7F3] text-xs font-semibold hover:bg-[#875F45] disabled:opacity-60">
            Add
          </button>
        </form>
        <div className="space-y-1.5">
          {plans.map((pl) => (
            <div key={pl.id} data-testid={`plan-row-${pl.id}`}
              className="flex items-center justify-between gap-2 text-xs bg-[#F8F7F3] rounded-xl px-3 py-2">
              <span className="text-[#3F5043]/85">{pl.title}{pl.start_date ? ` · starts ${pl.start_date}` : ""}</span>
              <select value={pl.status} data-testid={`plan-status-${pl.id}`}
                onChange={(e) => setPlanStatus(pl.id, e.target.value)}
                className="bg-white border border-[#BCB3A6]/40 rounded-full px-2 py-0.5 text-[10px] text-[#3F5043]">
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          ))}
          {plans.length === 0 && <div className="text-xs text-[#899480]">No treatment plans yet.</div>}
        </div>
      </div>
    </div>
  );
}
